import React from 'react';

import config from '../config';
import {
  ACADEMIC_APPLICATION_PATH,
  ACCOUNT_SETTINGS_PATHS,
  ACCOUNT_SETTINGS_USAGE_PAGE_PATHS,
  ADMIN_ORGANIZATION,
  AGENT_PATHS,
  ARTIFACT_VERSION_PATH,
  BENCHMARK_PATHS,
  ENTITY_LAUNCH_PATHS,
  GALLERY_INTERNAL_ADMIN_PATH,
  GALLERY_PATH,
  GALLERY_PATH_SEGMENT,
  GLOBAL_HISTORIC_USAGE_PATH,
  GLOBAL_HISTORIC_USAGE_REDIRECT_PATH,
  GLOBAL_HISTORIC_USAGE_REDIRECT_TO_PATH,
  GROUP_PATHS,
  HISTORIC_USAGE_PATH,
  HISTORIC_USAGE_REDIRECT_PATH,
  HISTORIC_USAGE_REDIRECT_TO_PATH,
  HOME_PATH,
  INFERENCE_COMPARE_PATH,
  INFERENCE_PATH,
  NO_ACCESS_PATH,
  OLD_GALLERY_DISCUSSION_EDIT_PATH,
  OLD_GALLERY_DISCUSSION_VIEW_PATH,
  OLD_REPORT_PAGE_EDIT_PATH,
  OLD_REPORT_PAGE_VIEW_PATH,
  ONBOARDING_FLOW_PATH,
  ORG_BILLING,
  ORG_DASHBOARD,
  ORG_SERVICE_ACCOUNTS,
  ORG_SETTINGS,
  ORG_SETTINGS_PATHS,
  ORG_TEAMS,
  ORG_USER_ACTIVITY,
  PROFILE_PATH,
  PROFILE_PROJECTS_PATH,
  PROFILE_REPORTS_PATH,
  PROFILE_STARS_PATH,
  PROJECT_BASE_PATH,
  PROJECT_PATHS,
  QUEUE_PATHS,
  QUICKSTART_PATH,
  REDIRECT_PATH,
  REGISTRY_PATHS,
  REPORT_PAGE_EDIT_PATH,
  REPORT_PAGE_VIEW_PATH,
  REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
  RUN_PATHS,
  STARTUP_PROGRAM,
  STORAGE_EXPLORER_TRACKED_PATH,
  SWEEP_PATHS,
  TEAM_MEMBERS_PATH,
  TEAM_SERVICE_ACCOUNTS_PATH,
  TEAM_SETTINGS_PATH,
  TRIAL_END_PATH,
  USER_SETTINGS_PATH,
} from './paths';
import {RouteData} from './types';

const IS_PRIVATE_ENV = config.ENVIRONMENT_IS_PRIVATE;

export const ROUTES_DATA: RouteData[] = [
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Home',
    },
    exact: true,
    isPrivateRoute: false,
    path: '/',
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Login',
    },
    isPrivateRoute: false,
    path: '/login',
    RouteComponent: React.lazy(() => import('../components/Login')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'LoginRedirect',
    },
    isPrivateRoute: false,
    path: '/login-redirect',
    RouteComponent: React.lazy(() => import('../components/LoginRedirect')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Logout',
    },
    isPrivateRoute: false,
    path: '/logout',
    RouteComponent: React.lazy(() => import('../components/Logout')),
    skipTracking: true,
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'HomePage',
      routeName: 'Home',
    },
    exact: true,
    isPrivateRoute: true,
    path: HOME_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'QuickStart',
    },
    exact: true,
    isPrivateRoute: false,
    path: QUICKSTART_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'FullyConnected',
    },
    isPrivateRoute: false,
    path: GALLERY_PATH,
    RouteComponent: React.lazy(() => import('../pages/FC')),
  },
  {
    path: '/fullyconnected',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    path: '/fc',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    path: '/gallery',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'InferenceModel',
    },
    isPrivateRoute: false,
    path: `${INFERENCE_PATH}/:collectionId/:modelId`,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Inference',
    },
    isPrivateRoute: false,
    path: `${INFERENCE_PATH}/:collectionId?`,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Inference Compare',
    },
    isPrivateRoute: false,
    path: INFERENCE_COMPARE_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    // the entity named "weave" is claimed by a past wandb employee
    // this is doing the quick thing to redirect this path to the weave site instead
    additionalProps: {
      allowIframes: false,
      routeName: 'WeaveEntity',
    },
    exact: true,
    isPrivateRoute: false,
    path: '/weave',
    RouteComponent: React.lazy(
      () => import('../pages/EntityPage/RedirectToWeaveSite')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'ErrorTestPage',
    },
    exact: true,
    isPrivateRoute: false,
    path: '/__test/simple_error',
    RouteComponent: React.lazy(() => import('../pages/ErrorTestPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'OnboardingFlow',
      routeName: 'OnboardingFlow',
    },
    isPrivateRoute: true,
    path: ONBOARDING_FLOW_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/OnboardingFlow/OnboardingFlow')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'StartupProgram',
      routeName: 'StartupProgram',
    },
    condition: !IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: STARTUP_PROGRAM,
    RouteComponent: React.lazy(() => import('../pages/StartupProgram')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'ChangePassword',
      routeName: 'ChangePassword',
    },
    isPrivateRoute: true,
    path: '/change-password',
    RouteComponent: React.lazy(() => import('../pages/ChangePassword')),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeCache: 'AuthorizePage',
      routeName: 'AuthorizePage',
    },
    isPrivateRoute: true,
    path: '/authorize',
    RouteComponent: React.lazy(() => import('../pages/AuthorizePage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'UserSettingsPage',
      routeName: 'UserSettings',
    },
    exact: true,
    isPrivateRoute: true,
    path: USER_SETTINGS_PATH,
    RouteComponent: React.lazy(
      () => import('../components/UserSettings/UserSettingsPage')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'AccountSettingsPage',
      routeName: 'AccountSettings',
    },
    condition: !IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ACCOUNT_SETTINGS_PATHS,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'AccountSettingsUsagePage',
      routeName: 'AccountSettingsUsagePage',
    },
    condition: !IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ACCOUNT_SETTINGS_USAGE_PAGE_PATHS,
    RouteComponent: React.lazy(
      () =>
        import(
          '../pages/Billing/AccountSettings/ManageUsagePage/ManageUsagePage'
        )
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'AuditLogs',
      routeName: 'AdminUsage',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: '/admin/audit-logs',
    RouteComponent: React.lazy(
      () => import('../pages/AuditLogs/AuditLogsPage')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'CreateNewTeam',
      routeName: 'CreateNewTeam',
    },
    condition: !IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: '/create-team',
    RouteComponent: React.lazy(() => import('../pages/CreateNewTeamPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'OrganizationAdmin',
      routeName: 'AdminOrganization',
    },
    isPrivateRoute: true,
    path: ADMIN_ORGANIZATION,
    RouteComponent: React.lazy(
      () => import('../wandb-admin/pages/OrganizationAdminPage')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'UsersAdmin',
      routeName: 'AdminUser',
    },
    isPrivateRoute: true,
    path: '/admin/users',
    RouteComponent: React.lazy(
      () => import('../pages/UsersAdminPage/UsersAdminPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'OrgDashboard',
      routeName: 'OrgDashboard',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_DASHBOARD,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'OrgTeams',
      routeName: 'OrgTeams',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_TEAMS,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'OrgServiceAccounts',
      routeName: 'OrgServiceAccounts',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_SERVICE_ACCOUNTS,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'OrgActivity',
      routeName: 'OrgActivity',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_USER_ACTIVITY,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'OrgBilling',
      routeName: 'OrgBilling',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_BILLING,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    path: '/org/roles',
    redirect: ORG_SETTINGS,
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'OrgSettingsPage',
      routeName: 'OrgSettingsPage',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ORG_SETTINGS_PATHS,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'GalleryAdmin',
      routeName: 'AdminFullyConnected',
    },
    isPrivateRoute: true,
    path: GALLERY_INTERNAL_ADMIN_PATH,
    RouteComponent: React.lazy(() => import('../pages/GalleryAdmin')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'RampAdmin',
      routeName: 'AdminRamp',
    },
    isPrivateRoute: true,
    path: '/admin/ramp',
    RouteComponent: React.lazy(() => import('../pages/Ramp/RampAdmin')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'GlueAdminPage',
      routeName: 'AdminGlue',
    },
    isPrivateRoute: true,
    path: '/admin/glue',
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'AdminDashboard',
      routeName: 'AdminHome',
    },
    isPrivateRoute: true,
    path: '/admin/home',
    RouteComponent: React.lazy(
      () => import('../wandb-admin/pages/HomeAdminPage')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'StorageExplorer',
    },
    exact: true,
    isPrivateRoute: false,
    path: [
      '/usage/:entityName',
      STORAGE_EXPLORER_TRACKED_PATH,
      '/usage/:entityName/:filePath*',
    ],
    RouteComponent: React.lazy(() => import('../pages/StorageExplorer')),
  },
  {
    path: '/storage/:entityName/tracked/:filePath*',
    redirect: STORAGE_EXPLORER_TRACKED_PATH,
  },
  {
    path: '/storage/:entityName/:filePath*',
    redirect: '/usage/:entityName/:filePath*',
  },
  {
    path: '/storage/:entityName',
    redirect: '/usage/:entityName',
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'NewBenchmark',
      routeName: 'NewBenchmark',
    },
    isPrivateRoute: true,
    path: '/new-benchmark',
    RouteComponent: React.lazy(() => import('../pages/NewBenchmark')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'NewBenchmarkPage',
      routeName: 'NewBenchmark',
    },
    isPrivateRoute: true,
    path: '/:entityname/benchmark/new',
    RouteComponent: React.lazy(() => import('../pages/NewBenchmark')),
  },
  {
    additionalProps: {allowIframes: false, routeName: 'Entity', tab: 'reports'},
    exact: true,
    isPrivateRoute: false,
    path: PROFILE_REPORTS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
    // note that here and below, we rely on the fact that we include the tab
    // when we log the route name
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Entity',
      tab: 'projects',
    },
    exact: true,
    isPrivateRoute: false,
    path: PROFILE_PROJECTS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  {
    additionalProps: {allowIframes: false, routeName: 'Entity', tab: 'stars'},
    exact: true,
    isPrivateRoute: false,
    path: PROFILE_STARS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  {
    additionalProps: {allowIframes: false, routeName: 'Entity', tab: 'members'},
    exact: true,
    isPrivateRoute: false,
    path: TEAM_MEMBERS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Entity',
      tab: 'service-accounts',
    },
    exact: true,
    isPrivateRoute: false,
    path: TEAM_SERVICE_ACCOUNTS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Entity',
      tab: 'settings',
    },
    exact: true,
    isPrivateRoute: false,
    path: TEAM_SETTINGS_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  // TODO: Remove! Keeping for backwards compatibility (email links, etc)
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Entity',
      tab: 'settings',
    },
    exact: true,
    isPrivateRoute: false,
    path: '/teams/:entityName',
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Registries',
    },
    exact: true,
    isPrivateRoute: false,
    path: REGISTRY_PATHS,
    RouteComponent: React.lazy(
      () => import('../components/Registries/RegistriesRouter')
    ),
  },
  {
    path: `${ARTIFACT_VERSION_PATH}/graph`,
    redirect: `${ARTIFACT_VERSION_PATH}/lineage`,
  },
  {
    path: '/models',
    redirect: '/registry/model',
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'EntityLaunch',
    },
    exact: true,
    isPrivateRoute: false,
    path: ENTITY_LAUNCH_PATHS,
    RouteComponent: React.lazy(
      () => import('../pages/HomePage/HomePageWithData')
    ),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'Run',
    },
    exact: true,
    isPrivateRoute: false,
    path: RUN_PATHS,
    RouteComponent: React.lazy(() => import('../pages/RunPage2')),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'Group',
    },
    exact: true,
    isPrivateRoute: false,
    path: GROUP_PATHS,
    RouteComponent: React.lazy(() => import('../pages/GroupPage')),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeCache: 'CreateSweepPage',
      routeName: 'CreateSweep',
    },
    exact: true,
    isPrivateRoute: true,
    path: '/:entityName/:projectName/create-sweep',
    RouteComponent: React.lazy(() => import('../pages/CreateSweepPage')),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'Sweep',
    },
    exact: true,
    isPrivateRoute: false,
    path: SWEEP_PATHS,
    RouteComponent: React.lazy(() => import('../pages/SweepPage')),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'Agent',
    },
    exact: true,
    isPrivateRoute: false,
    path: AGENT_PATHS,
    RouteComponent: React.lazy(
      () => import('../components/Launch/LaunchContent')
    ),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'Queue',
    },
    exact: true,
    isPrivateRoute: false,
    path: QUEUE_PATHS,
    RouteComponent: React.lazy(
      () => import('../components/Launch/LaunchContent')
    ),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'Benchmark',
    },
    exact: true,
    isPrivateRoute: false,
    path: BENCHMARK_PATHS,
    RouteComponent: React.lazy(
      () => import('../pages/Benchmark/BenchmarkPage')
    ),
  },
  {
    // This old edit report page route will be redirected to /reportlist
    additionalProps: {
      allowIframes: false,
      routeName: 'OldReportPageEdit',
    },
    exact: true,
    isPrivateRoute: false,
    path: OLD_REPORT_PAGE_EDIT_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    // This old view report page route will be redirected to /reportlist
    additionalProps: {
      allowIframes: false,
      routeName: 'OldReportPageView',
    },
    exact: true,
    isPrivateRoute: false,
    path: OLD_REPORT_PAGE_VIEW_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'ReportEdit',
    },
    exact: true,
    isPrivateRoute: false,
    path: REPORT_PAGE_EDIT_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'ReportView',
    },
    exact: true,
    isPrivateRoute: false,
    path: REPORT_PAGE_VIEW_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'ReportViewPublished',
    },
    exact: true,
    isPrivateRoute: false,
    path: REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'FullyConnectedDiscussionEdit',
    },
    exact: true,
    isPrivateRoute: false,
    path: OLD_GALLERY_DISCUSSION_EDIT_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'FullyConnectedDiscussionView',
    },
    exact: true,
    isPrivateRoute: false,
    path: OLD_GALLERY_DISCUSSION_VIEW_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/ReportPage/ReportPageRouter')
    ),
  },

  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'RepoInsightsPage',
      routeName: 'Repo',
    },
    exact: true,
    isPrivateRoute: true,
    path: '/repo/:repoName',
    RouteComponent: React.lazy(() => import('../pages/RepoInsightsPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'NoAccess',
    },
    exact: true,
    isPrivateRoute: false,
    path: NO_ACCESS_PATH,
    RouteComponent: React.lazy(() => import('../pages/NoAccessPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'GlobalHistoricUsage',
      routeName: 'GlobalHistoricUsage',
    },
    condition: IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: GLOBAL_HISTORIC_USAGE_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/HistoricUsage/GlobalHistoricUsage')
    ),
  },
  {
    condition: IS_PRIVATE_ENV,
    path: GLOBAL_HISTORIC_USAGE_REDIRECT_PATH,
    redirect: GLOBAL_HISTORIC_USAGE_REDIRECT_TO_PATH,
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'OrganizationHistoricUsage',
      routeName: 'OrganizationHistoricUsage',
    },
    condition: !IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: HISTORIC_USAGE_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/HistoricUsage/OrganizationHistoricUsage')
    ),
  },
  {
    condition: !IS_PRIVATE_ENV,
    path: HISTORIC_USAGE_REDIRECT_PATH,
    redirect: HISTORIC_USAGE_REDIRECT_TO_PATH,
  },
  {
    additionalProps: {
      allowIframes: true,
      routeName: 'Workspace',
    },
    exact: true,
    isPrivateRoute: false,
    path: PROJECT_PATHS,
    RouteComponent: React.lazy(() => import('../pages/ProjectPage')),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Redirect',
    },
    exact: true,
    isPrivateRoute: false,
    path: REDIRECT_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/RedirectPage/RedirectPage')
    ),
  },
  {
    // this is here to truncate extra path segments after the actual path
    path: PROJECT_BASE_PATH,
    redirect: PROJECT_BASE_PATH,
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'TrialEnd',
      routeName: 'TrialEnd',
    },
    condition: !IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: TRIAL_END_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/TrialEndPage/TrialEndPage')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeCache: 'AcademicApplication',
      routeName: 'AcademicApplication',
    },
    condition: !IS_PRIVATE_ENV,
    exact: true,
    isPrivateRoute: true,
    path: ACADEMIC_APPLICATION_PATH,
    RouteComponent: React.lazy(
      () => import('../pages/AcademicApplication/AcademicApplication')
    ),
  },
  {
    additionalProps: {
      allowIframes: false,
      routeName: 'Entity',
    },
    exact: true,
    isPrivateRoute: false,
    path: PROFILE_PATH,
    RouteComponent: React.lazy(() => import('../pages/EntityPage')),
  },
];
